import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { fetchDefenderDevices, fetchDefenderSummary } from '@/api/defender'
import { CustomerFilter } from '@/components/CustomerFilter'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { DefenderNav } from '@/components/defender/DefenderNav'
import { PostureLink } from '@/components/PostureIndicator'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { formatDateTime } from '@/lib/formatAgent'
import { TRAFFIC } from '@/lib/trafficLight'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

const SORTABLE = new Set([
  'hostname',
  'customer',
  'status',
  'defender_status',
  'defender_realtime',
  'defender_threats',
  'recent_detection_count',
  'collected_at',
])

const STATUS_CHART = [
  { status: 'green', label: TRAFFIC.green.label, colour: '#10b981' },
  { status: 'amber', label: TRAFFIC.amber.label, colour: '#f59e0b' },
  { status: 'red', label: TRAFFIC.red.label, colour: '#ef4444' },
  { status: 'grey', label: TRAFFIC.grey.label, colour: '#94a3b8' },
]

const LABEL_TO_STATUS = Object.fromEntries(STATUS_CHART.map((row) => [row.label, row.status]))
const STATUS_TO_LABEL = Object.fromEntries(STATUS_CHART.map((row) => [row.status, row.label]))
const STATUS_COLOUR = Object.fromEntries(STATUS_CHART.map((row) => [row.label, row.colour]))

function EnabledBadge({ value }) {
  const text = String(value || '').toLowerCase()
  if (!value) return <span className="text-muted-foreground">—</span>
  if (text.includes('enabled') || text === 'active') {
    return <Badge variant="success">{value}</Badge>
  }
  if (text.includes('disabled') || text.includes('inactive') || text.includes('off')) {
    return <Badge variant="destructive">{value}</Badge>
  }
  return <Badge variant="secondary">{value}</Badge>
}

function SortHeader({ column, label, sortBy, sortDir, onSort, className }) {
  const active = sortBy === column
  if (!SORTABLE.has(column)) {
    return <th className={cn('px-4 py-3 font-medium', className)}>{label}</th>
  }
  return (
    <th
      className={cn(
        'px-4 py-3 font-medium cursor-pointer select-none hover:text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

export function Defender() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [summary, setSummary] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState(null)
  const [showAllReporting, setShowAllReporting] = useState(false)
  const [sortBy, setSortBy] = useState('hostname')
  const [sortDir, setSortDir] = useState('asc')

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
  }, [canFilterCustomers])

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const attentionOnly = !showAllReporting
    try {
      const [summaryData, devicesData] = await Promise.all([
        fetchDefenderSummary(customerIds, { attention_only: attentionOnly }),
        fetchDefenderDevices(
          {
            page,
            page_size: PAGE_SIZE,
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            attention_only: attentionOnly,
          },
          customerIds,
        ),
      ])
      setSummary(summaryData)
      setItems(devicesData.items)
      setTotal(devicesData.total)
      setPages(devicesData.pages)
    } catch {
      setError('Could not load Windows Defender status.')
      setSummary(null)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [page, search, customerIds, statusFilter, showAllReporting, sortBy, sortDir])

  useEffect(() => {
    load()
  }, [load])

  const chartData = useMemo(() => {
    if (!summary) return []
    return STATUS_CHART.map((row) => ({
      label: row.label,
      value: summary[row.status] ?? 0,
    })).filter((row) => row.value > 0)
  }, [summary])

  function handleStatusFilter(nextStatus) {
    setStatusFilter((current) => (current === nextStatus ? null : nextStatus))
    setPage(1)
  }

  function handleChartClick(label) {
    const status = LABEL_TO_STATUS[label]
    if (!status) return
    handleStatusFilter(status)
  }

  function handleSort(column) {
    if (!SORTABLE.has(column)) return
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir(column === 'collected_at' || column === 'recent_detection_count' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const activeLabel = statusFilter ? STATUS_TO_LABEL[statusFilter] : null

  return (
    <div className="space-y-6">
      <DefenderNav />

      <p className="text-sm text-muted-foreground">
        Devices that need Defender attention by default. Devices covered by ThreatDown or
        third-party AV are hidden unless they have active threats or recent detections. Use
        the toggle below to show all reporting devices. Deploy the defender-detections script
        for the Detections tab.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border"
            checked={showAllReporting}
            onChange={(e) => {
              setShowAllReporting(e.target.checked)
              setPage(1)
            }}
          />
          Show all reporting devices
        </label>
        {!showAllReporting && summary?.reporting_devices > summary?.devices ? (
          <span className="text-xs text-muted-foreground">
            Showing {summary.devices} of {summary.reporting_devices} reporting devices
          </span>
        ) : null}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {canFilterCustomers && (
        <div className="max-w-md">
          <CustomerFilter
            id="defender-customer"
            value={customerIds}
            onChange={(v) => {
              setCustomerIds(v)
              setPage(1)
            }}
            customers={customers}
          />
        </div>
      )}

      {summary && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Device posture</CardTitle>
                {statusFilter ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStatusFilter(null)}
                  >
                    Clear filter
                  </Button>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                Click a slice or legend item to filter the device list.
              </p>
            </CardHeader>
            <CardContent>
              <SimplePieChart
                data={chartData}
                activeLabel={activeLabel}
                onItemClick={handleChartClick}
                colourFor={(entry) => STATUS_COLOUR[entry.label]}
                emptyMessage="No Defender posture data yet"
              />
            </CardContent>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Covered by ThreatDown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">{summary.threatdown_covered}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Covered by third-party AV
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">
                  {summary.third_party_covered ?? 0}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Hidden (alternate AV cover)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">
                  {showAllReporting ? 0 : (summary.hidden_by_cover ?? 0)}
                </p>
                {!showAllReporting && summary.hidden_by_cover > 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Covered by ThreatDown or third-party AV with no active threats or recent
                    detections
                  </p>
                ) : null}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Detections ({summary.detection_lookback_days}d)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold tabular-nums">{summary.recent_detections}</p>
                <Link
                  to="/defender/detections"
                  className="mt-1 inline-block text-xs text-primary hover:underline"
                >
                  Open detections
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      <div className="space-y-2 max-w-md">
        <Label htmlFor="defender-search">Search</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="defender-search"
            placeholder="Hostname, display name, customer…"
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        {statusFilter ? (
          <p className="text-xs text-muted-foreground">
            Showing {STATUS_TO_LABEL[statusFilter] || statusFilter} devices only.
          </p>
        ) : null}
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : items.length === 0 ? (
        <Card className="py-10 text-center text-sm text-muted-foreground">
          {!showAllReporting && summary?.hidden_by_cover > 0
            ? 'No devices need Defender attention. Enable “Show all reporting devices” to see covered devices.'
            : 'No Defender snapshots yet. Deploy the defender-dns RMM script from Atera.'}
        </Card>
      ) : (
        <div className="rounded-md border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                <SortHeader
                  column="hostname"
                  label="Device"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                {canFilterCustomers && (
                  <SortHeader
                    column="customer"
                    label="Customer"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                )}
                <SortHeader
                  column="status"
                  label="Status"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="defender_status"
                  label="Defender"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="defender_realtime"
                  label="Real-time"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="defender_threats"
                  label="Threats"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="recent_detection_count"
                  label="Detections"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="collected_at"
                  label="Collected"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                return (
                  <tr key={row.agent_id} className="border-b border-border last:border-0 align-top">
                    <td className="px-4 py-3">
                      <Link
                        to={`/agents/${row.agent_id}?tab=security`}
                        className="font-medium text-primary hover:underline"
                      >
                        {row.display_name || row.hostname || `Device ${row.agent_id}`}
                      </Link>
                      {row.display_name && row.hostname && row.display_name !== row.hostname ? (
                        <p className="text-xs text-muted-foreground">{row.hostname}</p>
                      ) : null}
                      {row.threatdown_covered ? (
                        <Badge variant="secondary" className="mt-1">
                          ThreatDown covered
                        </Badge>
                      ) : null}
                      {!row.threatdown_covered && row.third_party_covered && row.cover_label ? (
                        <Badge variant="secondary" className="mt-1">
                          {row.cover_label} covered
                        </Badge>
                      ) : null}
                    </td>
                    {canFilterCustomers && (
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.customer_name || '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <PostureLink
                        agentId={row.agent_id}
                        status={row.status}
                        tab="security"
                        label="Security"
                      />
                      {row.issues?.length ? (
                        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                          {row.issues.slice(0, 2).map((issue) => (
                            <li key={issue}>{issue}</li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <EnabledBadge value={row.defender_status} />
                    </td>
                    <td className="px-4 py-3">
                      <EnabledBadge value={row.defender_realtime} />
                    </td>
                    <td className="px-4 py-3">{row.defender_threats || '—'}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {row.recent_detection_count > 0 ? (
                        <Link
                          to="/defender/detections"
                          className="text-primary hover:underline"
                        >
                          {row.recent_detection_count}
                        </Link>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-muted-foreground">
                      {formatDateTime(row.collected_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground">
            Page {page} of {pages} · {total} device{total === 1 ? '' : 's'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
